import React from 'react';
import ReactDOM from 'react-dom/client';
import { PublicClientApplication } from '@azure/msal-browser';
import { BrowserRouter } from 'react-router-dom';
import App from './App';
import { msalConfig } from './authConfig';
import { AuthProvider, UseAuth } from './contexts/AuthContext';
import { DebugProvider } from './contexts/DebugContext';
import { SignalRProvider } from './contexts/SignalRContext';
import { WorkspaceProvider } from './contexts/WorkspaceContext';
import { WorkspaceErrorProvider } from './contexts/WorkspaceErrorContext';
import { ToastProvider } from './components/common/Toast/ToastContainer';
import { MsalProvider } from '@azure/msal-react';
import './index.css';

/** Wraps children with DebugProvider using current user from Auth (must be inside AuthProvider). */
const DebugProviderWrapper: React.FC<{
  children: React.ReactNode;
  allowedUserIds?: string[];
}> = ({ children, allowedUserIds }) => {
  const { currentUser } = UseAuth();
  return (
    <DebugProvider
      currentUserId={currentUser?.userId ?? null}
      allowedUserIds={allowedUserIds}
    >
      {children}
    </DebugProvider>
  );
};

  const ENABLE_CONSOLE_LOGS =
    import.meta.env.VITE_ENABLE_CONSOLE_LOGS === 'true';
  if (!ENABLE_CONSOLE_LOGS) {
  // Keep warn/error enabled for production diagnostics.
    console.log = () => {};
    console.debug = () => {};
    console.info = () => {};
  }


// Always create MSAL instance (required for MsalProvider)
// When bypassed, it will be a minimal instance that won't be used
const msalInstance = new PublicClientApplication(msalConfig);

// Initialize MSAL (needed for provider to work, even in bypass mode)
(async () => {
  await msalInstance.initialize();
})();

const root = ReactDOM.createRoot(document.getElementById('root')!);

// For production testing: disable StrictMode to replicate production behavior
// StrictMode causes double renders in dev which can hide timing issues
const DISABLE_STRICT_MODE = import.meta.env.VITE_DISABLE_STRICT_MODE === 'true';

// Always use MsalProvider (it's required for useMsal hook)
// When bypassed, AuthContext will ignore MSAL and use mock data
const AppWrapper = (
  <MsalProvider instance={msalInstance}>
    <BrowserRouter
      future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true,
      }}
    >
      <AuthProvider>
        <SignalRProvider>
          <ToastProvider>
            <WorkspaceErrorProvider>
              <DebugProviderWrapper>
                <WorkspaceProvider>
                  <App />
                </WorkspaceProvider>
              </DebugProviderWrapper>
            </WorkspaceErrorProvider>
          </ToastProvider>
        </SignalRProvider>
      </AuthProvider>
    </BrowserRouter>
  </MsalProvider>
);

root.render(
  DISABLE_STRICT_MODE ? (
    AppWrapper
  ) : (
    <React.StrictMode>{AppWrapper}</React.StrictMode>
  )
);
